/**
 * ErrorDialog
 *
 * Shared dialog for displaying structured error details with the ErrorTable.
 * Used by both VersionErrorDialog (preview panel) and OperationLogsDialog
 * (operations tab) for a consistent error viewing experience.
 */

import type { ReactNode } from 'react';
import {
  Badge,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@loader/ui';

import { ErrorTable } from './ErrorTable';
import type { ErrorRow } from './errorTableUtils';
import { formatErrorCount } from './errorTableUtils';

interface ErrorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  rows: ErrorRow[];
  isTruncated?: boolean;
  exportFileName?: string;
  footerMessage?: string;
}

export function ErrorDialog({
  open,
  onOpenChange,
  title,
  description,
  rows,
  isTruncated,
  exportFileName,
  footerMessage,
}: ErrorDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open && <div className="fixed inset-0 z-50 backdrop-blur-sm" aria-hidden />}
      <DialogContent className="sm:max-w-3xl gap-0 p-0">
        <DialogHeader className="px-6 pt-6 pb-4">
          <div className="flex items-center gap-3">
            <DialogTitle className="text-lg">{title}</DialogTitle>
            <Badge variant="destructive">{formatErrorCount(rows.length, isTruncated)}</Badge>
          </div>
          {description && <DialogDescription asChild>{description}</DialogDescription>}
        </DialogHeader>

        <ErrorTable
          rows={rows}
          isTruncated={isTruncated}
          exportFileName={exportFileName}
          footerMessage={footerMessage}
        />
      </DialogContent>
    </Dialog>
  );
}
